import React from "react";
import {
  Checkbox,
  CircularProgress,
  Grid,
  TableCell,
  TableRow,
  Tooltip,
} from "@mui/material";
import DensityMediumIcon from "@mui/icons-material/DensityMedium";
import moment from "moment";
import { useAreasContext } from "../../../../providers/OrganizationAdmin/AreaProvider";
import { useAuth } from "../../../../hooks";
import StatusDropdown from "../../../../common/StatusDropdown";
import {
  capitalizeFirstLetter,
  showNotification,
  truncateText,
} from "../../../../constants";
import {
  filterNonDeletedAreas,
  isAreaDeleted,
  restoreArea,
  softDeleteArea,
  updateArea,
} from "../../hooks/AreaHook";
import { Toast } from "../../../../common/alert";
import QuickCollectCell from "../QuickCollectCell/QuickCollectCell";

const AreaManagementTableRow = ({
  isItemSelected,
  row,
  provided,
  numberType,
  handleClickRow,
  labelId,
  handleNavigateToDetails,
  showDeletedColumn = false,
  // areaData,
  // handleUpdateAreaStatus,
}) => {
  const { currentUser } = useAuth();
  const { allAreas, setLoading, handleGetAllAreas } = useAreasContext();

  const handleUpdateAreasStatus = async (isActive) => {
    try {
      setLoading(true);
      let dataTemp = {
        isActive: isActive == "active" ? true : false,
        updatedAt: new Date(),
        updatedBy: currentUser?.userName,
      };
      console.log(isActive, row.id);
      let responseTemp = await updateArea(row.id, dataTemp);
      console.log("update area response:", responseTemp);
      await handleGetAllAreas();
      showNotification("Area updated successfully.");
    } catch (error) {
      Toast("error", error.message);
      console.log("error updating area", error.message);
    } finally {
      setLoading(false);
    }
  };

  const isDeleted = isAreaDeleted(row);

  // Highlight areas created today (browser local time).
  const isCreatedToday =
    !!row?.createdAt?.seconds &&
    moment.unix(row.createdAt.seconds).isSame(moment(), "day");

  // Superadmin-only: flip a row between deleted and restored.
  // Restoring puts the area at the end of the non-deleted list and leaves it
  // inactive, so it only becomes visible to users once it is re-activated.
  // Deleting renumbers the remaining non-deleted areas.
  const handleToggleDeleted = async () => {
    try {
      setLoading(true);
      const nonDeleted = filterNonDeletedAreas(allAreas).filter(
        (item) => item.id !== row.id
      );
      if (isDeleted) {
        await restoreArea(row.id, nonDeleted.length + 1, currentUser?.id);
        showNotification("Area restored successfully.");
      } else {
        await softDeleteArea(row.id, currentUser?.id);
        await Promise.all(
          nonDeleted.map((item, index) =>
            updateArea(item.id, { position: index + 1 })
          )
        );
        showNotification("Area deleted successfully.");
      }
      await handleGetAllAreas();
    } catch (error) {
      Toast("error", error.message);
      console.log("error toggling area deleted state", error.message);
    } finally {
      setLoading(false);
    }
  };

  const dropdownOptions = ["active", "inactive"];

  return (
    <TableRow
      ref={provided.innerRef}
      {...provided.draggableProps}
      hover
      // onClick={(event) => handleClickRow(event, row.id)}
      role="checkbox"
      aria-checked={isItemSelected}
      tabIndex={-1}
      key={row.id}
      selected={isItemSelected}
      sx={{
        cursor: "pointer",
        height: 30,
        background: isCreatedToday
          ? NEW_ROW_BACKGROUND
          : numberType === true
          ? "#F2F2F2"
          : "#fff",
      }}
    >
      <TableCell
        padding="checkbox"
        style={{ background: isCreatedToday ? NEW_ROW_BACKGROUND : "#fff" }}
        onClick={(event) => handleClickRow(event, row.id)}
      >
        <Checkbox
          color="primary"
          checked={isItemSelected}
          inputProps={{
            "aria-labelledby": labelId,
          }}
        />
      </TableCell>
            <TableCell
        {...provided.dragHandleProps}
        padding="none"
        align="left"
        style={tableCellStyles}
      >
        <Grid container>
          {row?.position}
          <DensityMediumIcon
            onClick={() => {}}
            fontSize={"small"}
            style={{ marginLeft: "auto", color: "#808080" }}
          />
        </Grid>
      </TableCell>
      <TableCell
        // component="th"
        id={labelId}
        scope="row"
        padding="none"
        onClick={handleNavigateToDetails}
        style={{
          ...(row?.name == "All"
            ? tableCellStyles
            : row?.name == "Nearby"
            ? tableCellStyles
            : tableTitleFieldStyles),
          width: 380,
          minWidth: 380,
        }}
      >
        {row?.name && truncateText(row.name, 60)}
      </TableCell>
      <TableCell
        align="left"
        padding="none"
        style={{
          ...tableCellStyles,
          minWidth: 170,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <StatusDropdown
          options={dropdownOptions}
          currentValue={row?.isActive ? "active" : "inactive"}
          onChange={handleUpdateAreasStatus}
          statusColors={areaStatusColors}
        />
      </TableCell>
      <TableCell padding="none" align="left" style={tableCellStyles}>
        {row?.numberOfQTs == null ? (
          <CircularProgress size={14} thickness={5} />
        ) : (
          row.numberOfQTs.length
        )}
        {/* {row?.quicktaleData?.length} */}
      </TableCell>
      <TableCell
        padding="none"
        align="left"
        style={{ ...tableCellStyles, minWidth: 180 }}
      >
        {row?.updatedAt?.seconds &&
          moment.unix(row.updatedAt.seconds).format("MM/DD/YYYY hh:mm:ss A")}
      </TableCell>
      <TableCell
        padding="none"
        align="left"
        style={{ ...tableCellStyles, minWidth: 240, width: 240 }}
      >
        <QuickCollectCell row={row} />
      </TableCell>
      {showDeletedColumn && (
        <TableCell padding="none" align="center" style={deletedCellStyles}>
          {isDeleted && (
            <Tooltip title="Deleted. Click to restore this area.">
              <span
                role="button"
                aria-label="Restore deleted area"
                onClick={handleToggleDeleted}
                style={deletedDotStyles}
              />
            </Tooltip>
          )}
        </TableCell>
      )}
    </TableRow>
  );
};

export default AreaManagementTableRow;

const tableTitleFieldStyles = {
  // fontWeight: 600,
  border: "1px solid #BABABA",
  color: "#00A7CD",
  fontFamily: "Avenir Book, sans-serif",
  paddingLeft: 10,
  height: 30,
  width: 150,
};

const tableCellStyles = {
  border: "1px solid #BABABA",
  cursor: "default",
  fontFamily: "Avenir Book, sans-serif",
  paddingLeft: 10,
  width: 100,
  height: 30,
};
// Pale green fill for rows created today.
const NEW_ROW_BACKGROUND = "#E8F5E9";

// Area Status colors (match the app's existing green/red status colors).
const areaStatusColors = {
  active: "#128D1F",
  inactive: "#D32F2F",
};

const deletedCellStyles = {
  border: "1px solid #BABABA",
  padding: 0,
  width: 12,
  minWidth: 12,
  maxWidth: 12,
  height: 30,
};

const deletedDotStyles = {
  display: "inline-block",
  width: 12,
  height: 12,
  borderRadius: "50%",
  backgroundColor: "#D32F2F",
  cursor: "pointer",
  verticalAlign: "middle",
};

const tableStatusCellStyles = {
  border: "1px solid #BABABA",
  cursor: "pointer",
  paddingLeft: 10,
  height: 30,
  width: 100,
  // fontWeight: 600,
};
